import moment from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	ArrowRightIcon,
	BellIcon,
	ConversationIcon,
	DeleteIcon,
	DialogueIcon,
	MoreAlternateIcon,
	RepeatIcon,
	TargetIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	ContextMenu,
	IconButton,
	ListItem,
	Paragraph,
	Popover,
	Spinner,
	Subheading,
	Todo,
	tokens,
} from '@treams/treams-ui';

import CycleMomentInstructionModal from '../../../../kakapo-ui-kit/components/cycles/CycleMomentInstructionModal';
import DateService from '../../../../kakapo-ui-services/services/common/DateService';

const AnimationWrapper = styled(ListItem)<{
	$isFinishedTask: boolean;
	$isLastFinishedTask: boolean;
}>`
	${(props) => (props.$isLastFinishedTask ? 'height: 62px;' : '')}

	animation: ${(props) =>
		props.$isFinishedTask && !props.$isLastFinishedTask ? 'SlideOutRow' : 'white'}
		4s ease-in-out forwards;

	${(props) =>
		props.$isLastFinishedTask
			? `
				> :first-child {
					animation: 1s fadeInOut 1s ease-in-out forwards;
				}

				@keyframes fadeInOut {
					0% {
						opacity: 1;
					}

					50% {
						opacity: 0;
						display: none;
					}

					100% {
						opacity: 0;
						display: none;
					}
				}`
			: ''}

	@keyframes SlideOutRow {
		0% {
			background-color: ${tokens.surface.white};
			height: 60px;
			overflow: hidden;
		}

		10% {
			background-color: ${tokens.success.background};
			overflow: hidden;
		}

		70% {
			height: 60px;
			overflow: hidden;
		}

		80% {
			height: 0;
			background-color: ${tokens.success.background};
			overflow: hidden;
		}

		100% {
			height: 0;
			background-color: ${tokens.success.background};
			overflow: hidden;
		}
	}
`;

const Deadline = styled.span<{ $overdue?: boolean }>`
	color: ${(props) => (props.$overdue ? tokens.error.text : tokens.text.secondary)};
`;

const Completed = styled.span`
	color: ${tokens.success.text};
`;

const EmptyState = styled(Paragraph)<{ $isLastFinishedTask: boolean }>`
	opacity: 0;

	${(props) =>
		props.$isLastFinishedTask
			? `
		animation: 1s fadeInOut2 1s ease-in-out forwards;

	@keyframes fadeInOut2 {
		0% {
			opacity: 0;
			display: none;
		}

		50% {
			opacity: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 62px;
		}

		100% {
			opacity: 1;
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 62px;
		}
	}
		`
			: ''}
`;

function taskIcon(task: graphql.InboxTask): ReactNode {
	if (task.type === graphql.InboxTaskType.FeedbackRequestPending) {
		return <DialogueIcon />;
	}
	if (task.type === graphql.InboxTaskType.EvaluationSubmissionOpen) {
		return <ConversationIcon />;
	}
	if (task.type === graphql.InboxTaskType.MomentStart) {
		switch (task.moment?.momentTemplate.type) {
			case graphql.MomentType.Evaluation:
				return <ConversationIcon />;
			case graphql.MomentType.Feedback:
				return <DialogueIcon />;
			case graphql.MomentType.Goals:
				return <TargetIcon />;
			case graphql.MomentType.Message:
				return <BellIcon />;
			default:
				return <RepeatIcon />;
		}
	}
	return <RepeatIcon />;
}

function taskDeadline(task: graphql.InboxTask) {
	if (task.type === graphql.InboxTaskType.FeedbackRequestPending) {
		return task.feedbackResponse?.dueDate;
	}
	if (task.type === graphql.InboxTaskType.EvaluationSubmissionOpen) {
		return task.evaluationSubmission?.deadline;
	}
	if (task.type === graphql.InboxTaskType.MomentStart) {
		return task.moment?.endDate;
	}
	return undefined;
}

function InboxTaskDeadline(props: { task: graphql.InboxTask; overdue?: boolean }) {
	const { t } = useTranslation();
	const deadline = taskDeadline(props.task);

	if (!deadline) {
		return null;
	}

	return (
		<Deadline $overdue={props.overdue}>
			{t('inbox.task.deadline', { deadline: moment(deadline).format('L') })}
		</Deadline>
	);
}

export default function InboxTaskItem({
	onClick,
	title,
	buttonTitle,
	date,
	task,
	tasks,
	finishedTask,
	instruction,
	refresh,
	subtitle,
	modalTitle,
	modalButtonTitle,
}: {
	onClick: () => void;
	title: string;
	buttonTitle: string;
	date: Date;
	task: graphql.InboxTask;
	tasks?: graphql.InboxTask[];
	finishedTask?: graphql.InboxTask;
	instruction?: graphql.LocalizedAttributedString;
	refresh: () => void;
	subtitle?: string;
	modalTitle?: string;
	modalButtonTitle?: string;
}) {
	const { t } = useTranslation();
	const [archive] = graphql.useArchiveInboxTaskMutation();

	const [menuOpen, setMenuOpen] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const [showInstruction, setShowInstruction] = useState(false);
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => {
			if (isFinishedTask) {
				localStorage.setItem(
					'inboxTasks',
					JSON.stringify(storedTasks.filter((task) => task.id !== finishedTask?.id)),
				);
			}
		}, 4000);

		return () => clearTimeout(timer);
	}, []);

	const isFinishedTask = finishedTask?.id === task.id;
	const storedTasks = JSON.parse(localStorage.getItem('inboxTasks') ?? '[]');
	const deadline = taskDeadline(task);
	const overdue = !!deadline && moment(deadline).isBefore(moment());
	const showExtra = isFinishedTask || !!deadline;
	const isLastFinishedTask = !!tasks && tasks.length === 0;

	const closeMenu = () => {
		setMenuOpen(false);
		setConfirming(false);
	};

	const sendArchive = async (id: string) => {
		await archive({ variables: { input: { id } } });
		setDeleting(true);
		closeMenu();
		refresh();
	};

	const handleClick = () => {
		if (menuOpen) {
			return;
		}

		if (
			task.status === graphql.InboxTaskStatus.Open &&
			instruction &&
			moment(task.createdAt) > moment('2021-11-10')
		) {
			setShowInstruction(true);
		} else {
			onClick();
		}
	};

	const createdAt = t('inbox.task.createdAt', {
		createdAt: DateService.daysAgo(moment(date).toDate(), new Date(), t),
	});

	return (
		<>
			<AnimationWrapper
				$isFinishedTask={isFinishedTask}
				$isLastFinishedTask={isLastFinishedTask}
				icon={isFinishedTask ? <Todo checked={true} /> : taskIcon(task)}
				title={title}
				meta={
					subtitle && !isFinishedTask ? (
						subtitle
					) : (
						<>
							<span>{createdAt}</span>
							{showExtra && <span>•</span>}
							{isFinishedTask ? (
								<Completed>{t('inbox.task.completed')}</Completed>
							) : (
								!overdue && <InboxTaskDeadline task={task} />
							)}
						</>
					)
				}
				metaAccent={
					!isFinishedTask && overdue ? <InboxTaskDeadline task={task} overdue /> : undefined
				}
				trailing={
					finishedTask?.id !== task.id ? (
						<>
							{deleting ? (
								<Spinner />
							) : (
								<Popover.Target
									visible={menuOpen}
									onDismiss={closeMenu}
									contents={
										confirming ? (
											<Box p={3} width={280} onClick={(event) => event.stopPropagation()}>
												<Subheading mb={1}>{t('inbox.task.remove.confirm.title')}</Subheading>
												<Paragraph mb={3}>{t('inbox.task.remove.confirm.description')}</Paragraph>
												<Flex justifyContent="flex-end">
													<Button
														type="secondary"
														small
														title={t('inbox.task.remove.confirm.cancelButton.title')}
														onClick={(event) => {
															event.stopPropagation();
															closeMenu();
														}}
													/>
													<Button
														small
														ml={2}
														title={t('inbox.task.remove.confirm.confirmButton.title')}
														onClick={(event) => {
															event.stopPropagation();
															sendArchive(task.id);
														}}
													/>
												</Flex>
											</Box>
										) : (
											<ContextMenu>
												<ContextMenu.Item
													icon={<DeleteIcon />}
													title={t('common.delete')}
													type="destructive"
													preventCloseOnClick
													onClick={() => setConfirming(true)}
												/>
											</ContextMenu>
										)
									}
								>
									<IconButton
										type="secondary"
										icon={<MoreAlternateIcon />}
										onClick={(event) => {
											event.stopPropagation();
											setMenuOpen(true);
										}}
									/>
								</Popover.Target>
							)}

							<Button title={buttonTitle} type="primary" rightIcon={<ArrowRightIcon />} />
						</>
					) : undefined
				}
				onClick={handleClick}
			>
				{finishedTask && tasks && tasks.length === 0 && (
					<EmptyState $isLastFinishedTask={tasks.length === 0}>
						{t('inbox.task.lastItem')}
					</EmptyState>
				)}
			</AnimationWrapper>
			{showInstruction && (
				<CycleMomentInstructionModal
					title={modalTitle ?? title}
					instruction={instruction}
					buttonTitle={modalButtonTitle ?? buttonTitle}
					onCancel={() => setShowInstruction(false)}
					onStart={() => onClick()}
				/>
			)}
		</>
	);
}
